import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Check, ChevronLeft, History, PenLine, RefreshCw, Search, Star, X } from 'lucide-react';
import { auswahlAusKnoten, auswahlFreitext, schluesselVon } from '../../services/mangelKatalog/auswahl';
import { nachHaeufigkeitSortieren } from '../../services/mangelKatalog/haeufigkeit';
import { useGesamtkatalog } from '../../services/mangelKatalog/laden';
import { useMerkliste } from '../../services/mangelKatalog/merkliste';
import { NAV_START, navigation, navigationZurueck } from '../../services/mangelKatalog/navigation';
import type { NavAktion, NavModus, NavZustand } from '../../services/mangelKatalog/navigation';
import { STANDARD_KATALOG } from '../../services/mangelKatalog/standard';
import { useKatalogStand, useMangelKatalog } from '../../services/mangelKatalog/store';
import { suchen } from '../../services/mangelKatalog/suche';
import type { AufgeloesterKnoten, KatalogAuswahl, KatalogSprache, MerkSchluessel } from '../../services/mangelKatalog/typen';
import {
    ansichtBerechnen,
    ariaBeschriftung,
    knotenAktion,
    merklisteAufloesen,
    naechsterPlatz,
    pfadBereinigen,
    platzAktion,
    trefferEintraege,
} from './auswahlAnsicht';
import type { ListenEintrag, PlatzAktion } from './auswahlAnsicht';
import { KatalogKachel } from './KatalogKachel';
import { KatalogPikto } from './KatalogPikto';
import { LANGDRUCK_KLASSEN, useLangdruck } from './langdruck';
import { RadialMenu } from './RadialMenu';
import { korbfaehig, korbSchluessel, korbUebernehmen, korbUmschalten } from './sammeln';
import type { KorbEintrag } from './sammeln';
import { katalogTexte } from './texte';

/**
 * Auswahl eines Standardmangels („Standardmangel per Torte“, Paket D):
 * Ebene 1 Torte der Kategorien → Ebene 2 Torte der Unterpunkte → Ebene 3
 * Kachelraster; darunter die Leiste Suche · Zuletzt · Favoriten · Freitext.
 *
 * - Die Tortenmitte bzw. der Zurück-Pfeil gehen einen Schritt zurück
 *   (navigationZurueck); in der Wurzel auf Seite 1 verlässt die Mitte die
 *   Auswahl (onVerlassen).
 * - Auswahl → merken (Zuletzt) → onAuswahl. Freitext-Kachel und
 *   Freitext-Knopf merken nichts.
 * - `steuerungRef` bekommt { zurueck } für die Android-Zurück-Taste bzw. Esc:
 *   false heißt „war in der Wurzel“, der Aufrufer verlässt dann die Auswahl.
 * - `startPfad` (Knoten-IDs, z. B. ['absturz']) wird nur beim Einhängen
 *   gelesen; für einen neuen Start die Komponente mit neuem `key` einhängen.
 *
 * Ableitungen (Seitenregel, Positionen, Tasten) stehen in auswahlAnsicht.ts.
 * `sprache` (28.09.): Katalog, Gesamtkatalog der Suche und feste Texte in
 * der Katalogsprache ('en' nur bei englischer Berichtssprache).
 * Reihenfolge nach Häufigkeit (28.09., haeufigkeit.ts): Torte und Kacheln
 * zeigen häufig Gewähltes vorn — mit dem Stand beim Einhängen, damit sich
 * während einer Auswahl nichts verschiebt. Der Admin kann das abschalten
 * (overlay.nachHaeufigkeit === false).
 * Mehrfachauswahl (Paket L, 30.09., sammeln.ts): Gedrückthalten einer Kachel
 * oder Listenzeile startet den Korb; solange er nicht leer ist, schaltet ein
 * Tipp um, die Sammelleiste zeigt „n gewählt · Übernehmen“ und Freitext ist
 * gesperrt. Übernehmen → EIN zusammengeführter Mangel an onAuswahl. Zurück in
 * der Wurzel verwirft zuerst den Korb.
 */

export interface KatalogSteuerung {
    /** Ein Schritt zurück; false = war schon in der Wurzel (nichts geändert). */
    zurueck: () => boolean;
    /** Ergänzung (Dialog, Ziffern 1–9): Position n der aktuellen Ansicht auslösen; false = dort nichts. */
    position?: (n: number) => boolean;
    /** Ergänzung (Dialog, Buchstabe tippen): in die Suche wechseln und `text` anhängen. */
    suche?: (text: string) => void;
}

export interface MangelKatalogAuswahlProps {
    onAuswahl: (a: KatalogAuswahl) => void;
    onVerlassen: () => void;
    /** Schritt ohne Katalog überspringen (Knopf „Freitext“ der Leiste). */
    onFreitext: () => void;
    darstellung: 'vollbild' | 'dialog';
    steuerungRef?: React.MutableRefObject<KatalogSteuerung | null>;
    startPfad?: string[];
    /** Katalogsprache (katalogSprache(language)); Standard deutsch. */
    sprache?: KatalogSprache;
}

/** Abschnitt des Gesamtkatalogs → Kategorie (für Suche und Merkliste). */
const ZUORDNUNG: Record<string, string> = STANDARD_KATALOG.abschnittZuordnung;
const MAX_TREFFER = 60;

interface Stand {
    nav: NavZustand;
    /** Knoten-ID, deren ⓘ-Einblendung offen ist. */
    info: string | null;
}

// ─── Bausteine ───────────────────────────────────────────────────────────────

const RUNDER_KNOPF =
    'flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-stone-700 hover:bg-stone-100 active:bg-stone-200';

export interface QuellenHinweisProps {
    fehler: boolean;
    onNochmal: () => void;
    /** Zweiter Satz, z. B. was bis dahin geht. */
    zusatz?: string;
    sprache?: KatalogSprache;
}

/** Hinweiszeile, solange der Gesamtkatalog nicht geladen ist. */
export const QuellenHinweis: React.FC<QuellenHinweisProps> = ({ fehler, onNochmal, zusatz, sprache }) => (
    <div role="status" className="flex items-center gap-2 border-b border-stone-200 bg-stone-100 px-4 py-1.5 text-[12px] font-semibold text-stone-600">
        <p className="min-w-0 flex-1 py-1">
            {katalogTexte(sprache).gesamtkatalogLaedt}{zusatz ? <span className="font-normal">; {zusatz}</span> : null}
        </p>
        {fehler && (
            <button
                type="button"
                onClick={onNochmal}
                className="flex min-h-11 shrink-0 items-center gap-1.5 rounded-lg px-2 text-stone-800 hover:bg-stone-200 active:bg-stone-300"
            >
                <RefreshCw size={14} aria-hidden="true" />
                {katalogTexte(sprache).erneutVersuchen}
            </button>
        )}
    </div>
);

export interface KatalogListeProps {
    eintraege: readonly ListenEintrag[];
    favoriten: ReadonlySet<MerkSchluessel>;
    onWahl: (e: ListenEintrag) => void;
    onFavorit?: (s: MerkSchluessel) => void;
    /** Kurzer Satz für die leere Liste. */
    leerText: string;
    sprache?: KatalogSprache;
    /** Mehrfachauswahl: Gedrückthalten einer Zeile. */
    onHalten?: (e: ListenEintrag) => void;
    /** Nur im Sammelmodus: Schlüssel im Korb (Haken, aria-pressed). */
    gewaehlt?: ReadonlySet<MerkSchluessel>;
}

const nichts = () => undefined;

interface ListenKnopfProps {
    eintrag: ListenEintrag;
    platz: number;
    onWahl: (e: ListenEintrag) => void;
    onHalten?: (e: ListenEintrag) => void;
    /** undefined = kein Sammelmodus. */
    gewaehlt?: boolean;
}

/** Hauptknopf einer Listenzeile; mit onHalten auch Gedrückthalten. */
const ListenKnopf: React.FC<ListenKnopfProps> = ({ eintrag: e, platz, onWahl, onHalten, gewaehlt }) => {
    const druck = useLangdruck({ onHalten: onHalten ? () => onHalten(e) : nichts, onKurz: () => onWahl(e) });
    return (
        <button
            type="button"
            data-platz={platz}
            {...(onHalten ? druck : { onClick: () => onWahl(e) })}
            aria-pressed={gewaehlt}
            className={`flex min-h-14 min-w-0 flex-1 items-center gap-3 px-3 py-2 text-left ${onHalten ? LANGDRUCK_KLASSEN : ''} ${
                gewaehlt ? 'bg-stone-100 hover:bg-stone-200 active:bg-stone-300' : 'hover:bg-stone-50 active:bg-stone-200'
            }`}
        >
            <span className="relative shrink-0">
                <KatalogPikto schluessel={e.auswahl.pikto} groesse={28} />
                {gewaehlt && (
                    <span aria-hidden="true" className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-stone-800 text-white">
                        <Check size={13} strokeWidth={3} />
                    </span>
                )}
            </span>
            <span className="min-w-0 flex-1">
                <span className="line-clamp-2 text-[14px] font-semibold leading-snug text-stone-800">{e.titel}</span>
                {e.pfad && (
                    <span className="mt-0.5 flex items-baseline gap-2 text-[12px] text-stone-500">
                        <span className="min-w-0 truncate">{e.pfad}</span>
                    </span>
                )}
            </span>
        </button>
    );
};

/** Trefferliste (Suche, Zuletzt, Favoriten): Pikto, Titel, Pfad, Stern. */
export const KatalogListe: React.FC<KatalogListeProps> = ({ eintraege, favoriten, onWahl, onFavorit, leerText, sprache, onHalten, gewaehlt }) => {
    const t = katalogTexte(sprache);
    if (!eintraege.length) {
        return <p className="px-6 py-8 text-center text-[13px] font-semibold text-stone-500">{leerText}</p>;
    }
    return (
        <ul className="divide-y divide-stone-200 border-b border-stone-200 bg-white">
            {eintraege.map((e, i) => {
                const fav = favoriten.has(e.schluessel);
                return (
                    <li key={e.schluessel} className="flex items-stretch">
                        <ListenKnopf
                            eintrag={e}
                            platz={i}
                            onWahl={onWahl}
                            onHalten={onHalten && korbfaehig(e.auswahl) ? onHalten : undefined}
                            gewaehlt={gewaehlt ? gewaehlt.has(e.schluessel) : undefined}
                        />
                        {onFavorit && (
                            <button
                                type="button"
                                aria-label={fav ? t.favoritEntfernen(e.titel) : t.alsFavoritMerken(e.titel)}
                                aria-pressed={fav}
                                onClick={() => onFavorit(e.schluessel)}
                                className={`flex min-w-11 shrink-0 items-center justify-center hover:bg-stone-50 active:bg-stone-200 ${fav ? 'text-amber-600' : 'text-stone-400'}`}
                            >
                                <Star size={18} aria-hidden="true" fill={fav ? 'currentColor' : 'none'} />
                            </button>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

interface LeistenKnopfProps {
    /** Lucide-Komponente (kein Element) — daher bewusst nicht `icon` (Hausregel, iconProp.test.ts). */
    symbol: LucideIcon;
    beschriftung: string;
    aktiv?: boolean;
    onClick: () => void;
    gesperrt?: boolean;
}

const LeistenKnopf: React.FC<LeistenKnopfProps> = ({ symbol: Icon, beschriftung, aktiv, onClick, gesperrt }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={gesperrt}
        aria-pressed={aktiv}
        className={`flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1 text-[11px] font-semibold disabled:opacity-40 ${
            aktiv ? 'bg-stone-200 text-stone-900' : 'text-stone-600 hover:bg-stone-100 active:bg-stone-200'
        }`}
    >
        <Icon size={20} aria-hidden="true" />
        {beschriftung}
    </button>
);

export interface InfoEinblendungProps {
    knoten: AufgeloesterKnoten;
    onSchliessen: () => void;
    onUebernehmen: () => void;
    sprache?: KatalogSprache;
    /** Nur im Sammelmodus: liegt im Korb → „Aus der Auswahl entfernen“, sonst „Hinzufügen“. */
    imKorb?: boolean;
}

/** ⓘ einer Kachel: Überschrift, Beschreibung, Vorschrift — sonst nichts. */
export const InfoEinblendung: React.FC<InfoEinblendungProps> = ({ knoten, onSchliessen, onUebernehmen, sprache, imKorb }) => {
    const t = katalogTexte(sprache);
    const v = knoten.vorlage;
    const kurz = ariaBeschriftung(knoten.kurz) || knoten.name;
    return (
        <section
            aria-label={t.hinweisZu(kurz)}
            className="absolute inset-x-3 bottom-3 max-h-[80%] overflow-y-auto rounded-xl border border-stone-400 bg-white p-3"
        >
            <div className="flex items-start gap-2">
                <KatalogPikto schluessel={knoten.pikto} groesse={28} className="mt-1" />
                <h4 className="min-w-0 flex-1 pt-1 text-[14px] font-semibold leading-snug text-stone-900">{v?.titel || kurz}</h4>
                <button type="button" autoFocus onClick={onSchliessen} aria-label={t.hinweisSchliessen} className={`${RUNDER_KNOPF} -mr-1 -mt-1`}>
                    <X size={18} aria-hidden="true" />
                </button>
            </div>
            {v?.text && <p className="mt-2 text-[13px] leading-snug text-stone-700">{v.text}</p>}
            {v?.vorschrift && (
                <p className="mt-2 text-[12px] leading-snug text-stone-700">
                    <span className="font-semibold">{t.vorschrift} </span>
                    {v.vorschrift}
                </p>
            )}
            <button
                type="button"
                onClick={onUebernehmen}
                className="mt-3 min-h-11 w-full rounded-lg bg-stone-800 px-3 text-[14px] font-semibold text-white hover:bg-stone-700 active:bg-stone-600"
            >
                {imKorb === undefined ? t.uebernehmen : imKorb ? t.ausAuswahlEntfernen : t.hinzufuegen}
            </button>
        </section>
    );
};

export interface SammelLeisteProps {
    /** Kurznamen der Punkte im Korb, in Auswahlreihenfolge. */
    namen: readonly string[];
    onVerwerfen: () => void;
    onUebernehmen: () => void;
    sprache?: KatalogSprache;
}

/** Mehrfachauswahl: [×] · „2 gewählt“ + Namen · [Übernehmen]. */
export const SammelLeiste: React.FC<SammelLeisteProps> = ({ namen, onVerwerfen, onUebernehmen, sprache }) => {
    const t = katalogTexte(sprache);
    return (
        <section aria-label={t.gewaehltAnzahl(namen.length)} className="flex shrink-0 items-center gap-2 border-t border-stone-300 bg-white px-2 py-1.5">
            <button type="button" onClick={onVerwerfen} aria-label={t.auswahlVerwerfen} title={t.auswahlVerwerfen} className={RUNDER_KNOPF}>
                <X size={20} aria-hidden="true" />
            </button>
            <div role="status" className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold leading-snug text-stone-900">{t.gewaehltAnzahl(namen.length)}</p>
                <p className="truncate text-[12px] leading-snug text-stone-500">{namen.join(' · ')}</p>
            </div>
            <button
                type="button"
                onClick={onUebernehmen}
                className="min-h-11 shrink-0 rounded-lg bg-stone-800 px-4 text-[14px] font-semibold text-white hover:bg-stone-700 active:bg-stone-600"
            >
                {t.uebernehmen}
            </button>
        </section>
    );
};

// ─── Auswahl ─────────────────────────────────────────────────────────────────

const RASTER_TASTEN = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'];

export const MangelKatalogAuswahl: React.FC<MangelKatalogAuswahlProps> = ({
    onAuswahl,
    onVerlassen,
    onFreitext,
    darstellung,
    steuerungRef,
    startPfad,
    sprache = 'de',
}) => {
    const { nutzerId, overlay, haeufigkeit } = useKatalogStand();
    const katalog = useMangelKatalog(sprache);
    // Häufigkeit UND Schalter beim Einhängen einfrieren: kommt beides erst während
    // der Auswahl an (Laden nach dem Login), springt nichts (Codex-Review 28.09.).
    const [haeufigkeitBeimOeffnen] = useState(() => (overlay?.nachHaeufigkeit === false ? null : haeufigkeit));
    const reihenfolge = useMemo(() => nachHaeufigkeitSortieren(katalog, haeufigkeitBeimOeffnen), [katalog, haeufigkeitBeimOeffnen]);
    const gesamt = useGesamtkatalog(sprache);
    const merk = useMerkliste(nutzerId);
    const t = katalogTexte(sprache);

    const [stand, setStand] = useState<Stand>(() => ({
        nav: navigation(NAV_START, { typ: 'start', pfad: pfadBereinigen(reihenfolge, startPfad ?? []) }),
        info: null,
    }));
    // Synchrone Kopie für Zurück-Taste/Esc: zwei schnelle Drücke sehen beide den neuesten Stand.
    const standRef = useRef(stand);
    const setzen = useCallback((neu: Stand) => {
        standRef.current = neu;
        setStand(neu);
    }, []);
    const { nav, info } = stand;

    // Mehrfachauswahl: Korb mit synchroner Kopie (Zurück-Taste, schnelle Tipps).
    const [korb, setKorb] = useState<readonly KorbEintrag[]>([]);
    const korbRef = useRef(korb);
    const korbSetzen = useCallback((neu: readonly KorbEintrag[]) => {
        korbRef.current = neu;
        setKorb(neu);
    }, []);
    const sammeln = korb.length > 0;
    const imKorb = useMemo(() => korbSchluessel(korb), [korb]);

    const rootRef = useRef<HTMLDivElement>(null);
    const suchfeldRef = useRef<HTMLInputElement>(null);

    const ansicht = useMemo(() => ansichtBerechnen(reihenfolge, nav.pfad, nav.seite, sprache), [reihenfolge, nav.pfad, nav.seite, sprache]);
    const favoriten = useMemo(() => new Set(merk.favoriten), [merk.favoriten]);
    const suchtext = useDeferredValue(nav.suchtext);
    const treffer = useMemo(
        () => (nav.modus === 'suche' ? trefferEintraege(suchen(suchtext, katalog, gesamt.quelle, ZUORDNUNG, MAX_TREFFER), katalog) : []),
        [nav.modus, suchtext, katalog, gesamt.quelle],
    );
    const merkliste = useMemo(() => {
        if (nav.modus === 'zuletzt') return merklisteAufloesen(merk.zuletzt, katalog, gesamt.quelle, ZUORDNUNG);
        if (nav.modus === 'favoriten') return merklisteAufloesen(merk.favoriten, katalog, gesamt.quelle, ZUORDNUNG);
        return null;
    }, [nav.modus, merk.zuletzt, merk.favoriten, katalog, gesamt.quelle]);
    const listeJetzt: readonly ListenEintrag[] = nav.modus === 'suche' ? treffer : merkliste?.eintraege ?? [];
    const infoKnoten = info ? katalog.knoten.get(info) : undefined;

    // ─── Aktionen ────────────────────────────────────────────────────────────

    const aktion = useCallback(
        (a: NavAktion) => {
            const s = standRef.current;
            setzen({ nav: navigation(s.nav, a), info: null });
        },
        [setzen],
    );

    const zurueck = useCallback((): boolean => {
        const s = standRef.current;
        if (s.info) {
            setzen({ ...s, info: null });
            return true;
        }
        const neu = navigationZurueck(s.nav);
        if (neu) {
            setzen({ nav: neu, info: null });
            return true;
        }
        // Wurzel: erst die Mehrfachauswahl verwerfen, dann verlassen.
        if (korbRef.current.length) {
            korbSetzen([]);
            return true;
        }
        return false;
    }, [setzen, korbSetzen]);

    const zurueckOderVerlassen = () => {
        if (!zurueck()) onVerlassen();
    };

    /** Kurzname für die Sammelleiste: Kachel-Kurztext, sonst Überschrift. */
    const kurzVon = (a: KatalogAuswahl): string => {
        const k = a.katalogKey ? katalog.knoten.get(a.katalogKey) : undefined;
        return (k && (ariaBeschriftung(k.kurz) || k.name)) || a.vorlage?.titel || '';
    };

    /** In den Korb bzw. heraus (Gedrückthalten; im Sammelmodus auch Tippen). */
    const umschalten = (a: KatalogAuswahl) => {
        const s = schluesselVon(a);
        if (!s) return;
        korbSetzen(korbUmschalten(korbRef.current, { schluessel: s, auswahl: a, kurz: kurzVon(a) }));
    };

    const waehlen = (a: KatalogAuswahl) => {
        if (korbRef.current.length) {
            if (korbfaehig(a)) umschalten(a);
            return;
        }
        const s = schluesselVon(a);
        if (s) merk.merken(s);
        onAuswahl(a);
    };

    /** Korb → EIN Mangel; jeder Punkt kommt in „Zuletzt“. */
    const uebernehmen = () => {
        const teile = korbRef.current;
        const a = korbUebernehmen(teile);
        if (!a) return;
        for (const k of teile) merk.merken(k.schluessel);
        korbSetzen([]);
        onAuswahl(a);
    };

    const ausfuehren = (p: PlatzAktion | null): boolean => {
        if (!p) return false;
        if (p.typ === 'oeffnen') aktion({ typ: 'oeffnen', id: p.id });
        else if (p.typ === 'weiter') aktion({ typ: 'weiter' });
        else if (p.typ === 'waehlen') waehlen(auswahlAusKnoten(p.knoten));
        else if (korbRef.current.length) return false;
        else waehlen(auswahlFreitext(p.kategorie));
        return true;
    };

    const modusUmschalten = (m: NavModus) => aktion({ typ: 'modus', modus: nav.modus === m ? 'torte' : m });

    const infoOeffnen = (id: string) => setzen({ ...standRef.current, info: id });

    const position = (n: number): boolean => {
        if (nav.modus === 'torte') return ausfuehren(platzAktion(ansicht, katalog, n - 1));
        const e = listeJetzt[n - 1];
        if (!e) return false;
        waehlen(e.auswahl);
        return true;
    };

    const suche = (text: string) => {
        const s = standRef.current;
        const neu = s.nav.modus === 'suche' ? s.nav.suchtext + text : text;
        setzen({ nav: navigation(s.nav, { typ: 'suchtext', text: neu }), info: null });
        // Schon eingehängt: Fokus hinein; sonst übernimmt autoFocus beim Einhängen.
        suchfeldRef.current?.focus();
    };

    // Steuerung für den Aufrufer (Android-Zurück, Dialog-Tastatur): nach jedem Rendern frisch.
    useEffect(() => {
        if (!steuerungRef) return;
        const s: KatalogSteuerung = { zurueck, position, suche };
        steuerungRef.current = s;
        return () => {
            if (steuerungRef.current === s) steuerungRef.current = null;
        };
    });

    // Ging der Fokus mit dem alten Inhalt verloren (Ansicht gewechselt), landet er
    // auf dem ersten Platz der neuen Ansicht — Tastatur und Screenreader bleiben dran.
    const ansichtSchluessel = `${nav.modus}|${ansicht.pfad.join('/')}|${ansicht.seite}|${info ?? ''}`;
    useEffect(() => {
        const root = rootRef.current;
        if (!root || typeof document === 'undefined') return;
        const aktiv = document.activeElement;
        if (aktiv && aktiv !== document.body && aktiv.isConnected) return;
        root.querySelector<HTMLElement | SVGElement>('[data-platz]')?.focus({ preventScroll: true });
    }, [ansichtSchluessel]);

    const rasterPfeile = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (ansicht.art !== 'kacheln' || !RASTER_TASTEN.includes(e.key)) return;
        const kachel = e.target instanceof Element ? e.target.closest('[data-raster]') : null;
        const roh = kachel?.getAttribute('data-raster');
        const aktuell = roh === null || roh === undefined ? null : Number(roh);
        const ziel = naechsterPlatz(aktuell, e.key, ansicht.plaetze.map((p) => p.art !== 'leer'), 'raster');
        if (ziel === null) return;
        const el = e.currentTarget.querySelector<HTMLElement>(`[data-platz="${ziel}"]`);
        if (!el) return;
        e.preventDefault();
        el.focus();
    };

    // ─── Oberfläche ──────────────────────────────────────────────────────────

    const vollbild = darstellung === 'vollbild';
    const seitenText = ansicht.seiten > 1 ? t.seite(ansicht.seite + 1, ansicht.seiten) : null;
    const zurueckKnopf = (
        <button type="button" onClick={zurueckOderVerlassen} aria-label={t.zurueck} className={RUNDER_KNOPF}>
            <ChevronLeft size={22} aria-hidden="true" />
        </button>
    );

    let kopf: React.ReactNode;
    let inhalt: React.ReactNode;

    if (nav.modus === 'suche') {
        kopf = (
            <>
                {zurueckKnopf}
                <input
                    ref={suchfeldRef}
                    autoFocus
                    type="text"
                    inputMode="search"
                    enterKeyHint="search"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    value={nav.suchtext}
                    onChange={(e) => aktion({ typ: 'suchtext', text: e.target.value })}
                    onKeyDown={(e) => {
                        if (e.key !== 'Enter' || !treffer[0]) return;
                        e.preventDefault();
                        waehlen(treffer[0].auswahl);
                    }}
                    placeholder={t.suchPlatzhalter}
                    aria-label={t.suchAria}
                    className="h-11 min-w-0 flex-1 rounded-lg border border-stone-300 bg-white px-3 text-[15px] text-stone-900 placeholder:text-stone-400"
                />
            </>
        );
        inhalt = (
            <>
                {!gesamt.quelle && <QuellenHinweis fehler={gesamt.fehler} onNochmal={gesamt.nochmal} zusatz={t.bisDahinNurTorte} sprache={sprache} />}
                {!nav.suchtext.trim() ? (
                    <p className="px-6 py-8 text-center text-[13px] font-semibold text-stone-500">
                        {t.sucheLeer}
                    </p>
                ) : (
                    <KatalogListe
                        eintraege={treffer}
                        favoriten={favoriten}
                        onWahl={(e) => waehlen(e.auswahl)}
                        onFavorit={merk.favoritUmschalten}
                        leerText={t.keineTreffer}
                        sprache={sprache}
                        onHalten={(e) => umschalten(e.auswahl)}
                        gewaehlt={sammeln ? imKorb : undefined}
                    />
                )}
            </>
        );
    } else if (nav.modus === 'zuletzt' || nav.modus === 'favoriten') {
        const zuletzt = nav.modus === 'zuletzt';
        kopf = (
            <>
                {zurueckKnopf}
                <h3 className="min-w-0 flex-1 truncate text-[15px] font-semibold text-stone-800">{zuletzt ? t.zuletztGewaehlt : t.favoriten}</h3>
            </>
        );
        inhalt = (
            <>
                {merkliste?.wartetAufQuelle && <QuellenHinweis fehler={gesamt.fehler} onNochmal={gesamt.nochmal} zusatz={t.einigeEintraegeDanach} sprache={sprache} />}
                <KatalogListe
                    eintraege={merkliste?.eintraege ?? []}
                    favoriten={favoriten}
                    onWahl={(e) => waehlen(e.auswahl)}
                    onFavorit={merk.favoritUmschalten}
                    leerText={zuletzt ? t.nochNichtsGewaehlt : t.nochKeineFavoriten}
                    sprache={sprache}
                    onHalten={(e) => umschalten(e.auswahl)}
                    gewaehlt={sammeln ? imKorb : undefined}
                />
            </>
        );
    } else if (ansicht.art === 'torte') {
        kopf = (
            <>
                <h3 className="min-w-0 flex-1 truncate py-2.5 pl-2 text-[15px] font-semibold text-stone-800">{ansicht.titel}</h3>
                {seitenText && <span className="shrink-0 pr-2 text-[12px] font-semibold text-stone-500">{seitenText}</span>}
            </>
        );
        inhalt = (
            <div className="flex min-h-full items-center justify-center p-3">
                <RadialMenu
                    titel={ansicht.titel}
                    stuecke={ansicht.stuecke}
                    mitteBeschriftung={ansicht.mitteBeschriftung}
                    mitteAriaLabel={ansicht.mitteAriaLabel}
                    onWahl={(id) => ausfuehren(knotenAktion(katalog, id))}
                    onWeiter={() => aktion({ typ: 'weiter' })}
                    onMitte={zurueckOderVerlassen}
                    className={vollbild ? 'w-[min(92vw,360px)]' : 'w-[320px]'}
                    sprache={sprache}
                />
            </div>
        );
    } else {
        kopf = (
            <>
                {zurueckKnopf}
                <h3 className="min-w-0 flex-1 truncate text-[15px] font-semibold text-stone-800">{ansicht.titel}</h3>
                {seitenText && <span className="shrink-0 pr-2 text-[12px] font-semibold text-stone-500">{seitenText}</span>}
            </>
        );
        inhalt = (
            <div role="group" aria-label={ansicht.titel} onKeyDown={rasterPfeile} className="mx-auto grid w-full max-w-[30rem] grid-cols-3 gap-2 p-3">
                {ansicht.plaetze.map((p, i) => {
                    let kachel: React.ReactNode;
                    if (p.art === 'mangel') {
                        const s = `knoten:${p.knoten.id}`;
                        kachel = (
                            <KatalogKachel
                                art="mangel"
                                platz={i}
                                kurz={ariaBeschriftung(p.knoten.kurz) || p.knoten.name}
                                pikto={p.knoten.pikto}
                                favorit={favoriten.has(s)}
                                onWahl={() => ausfuehren({ typ: 'waehlen', knoten: p.knoten })}
                                onInfo={() => infoOeffnen(p.knoten.id)}
                                onFavorit={() => merk.favoritUmschalten(s)}
                                onHalten={() => umschalten(auswahlAusKnoten(p.knoten))}
                                gewaehlt={sammeln ? imKorb.has(s) : undefined}
                                sprache={sprache}
                            />
                        );
                    } else if (p.art === 'gruppe') {
                        kachel = (
                            <KatalogKachel
                                art="gruppe"
                                platz={i}
                                kurz={p.knoten.name}
                                pikto={p.knoten.pikto}
                                onWahl={() => aktion({ typ: 'oeffnen', id: p.knoten.id })}
                            />
                        );
                    } else if (p.art === 'weiter') {
                        kachel = <KatalogKachel art="weiter" platz={i} rest={p.rest} onWahl={() => aktion({ typ: 'weiter' })} sprache={sprache} />;
                    } else if (p.art === 'freitext') {
                        const kategorie = p.kategorie;
                        kachel = (
                            <KatalogKachel
                                art="freitext"
                                platz={i}
                                kurz={t.freitextIn(kategorie.name)}
                                onWahl={() => ausfuehren({ typ: 'freitext', kategorie })}
                                gesperrt={sammeln}
                            />
                        );
                    } else {
                        kachel = <KatalogKachel art="leer" platz={i} />;
                    }
                    return (
                        <div key={i} data-raster={i} className="contents">
                            {kachel}
                        </div>
                    );
                })}
                {!sammeln && ansicht.plaetze.some((p) => p.art === 'mangel') && (
                    <p className="col-span-3 px-2 pt-1 text-center text-[11px] font-semibold leading-snug text-stone-500">{t.mehrfachTipp}</p>
                )}
            </div>
        );
    }

    return (
        <div
            ref={rootRef}
            data-darstellung={darstellung}
            className={`flex h-full min-h-0 w-full flex-col bg-stone-50 text-stone-900 ${vollbild ? 'flex-1' : ''}`}
        >
            <div className="flex min-h-12 shrink-0 items-center gap-1 border-b border-stone-200 bg-white px-2 py-0.5">{kopf}</div>
            <div className="relative flex min-h-0 flex-1 flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{inhalt}</div>
                {infoKnoten && nav.modus === 'torte' && (
                    <InfoEinblendung
                        knoten={infoKnoten}
                        onSchliessen={() => setzen({ ...standRef.current, info: null })}
                        onUebernehmen={() => {
                            if (!sammeln) {
                                ausfuehren(knotenAktion(katalog, infoKnoten.id));
                                return;
                            }
                            umschalten(auswahlAusKnoten(infoKnoten));
                            setzen({ ...standRef.current, info: null });
                        }}
                        sprache={sprache}
                        imKorb={sammeln ? imKorb.has(`knoten:${infoKnoten.id}`) : undefined}
                    />
                )}
            </div>
            {sammeln && (
                <SammelLeiste namen={korb.map((k) => k.kurz)} onVerwerfen={() => korbSetzen([])} onUebernehmen={uebernehmen} sprache={sprache} />
            )}
            <nav
                aria-label={t.leisteAria}
                className={`grid shrink-0 grid-cols-4 gap-1 border-t border-stone-200 bg-white px-2 pt-1.5 ${
                    vollbild ? 'pb-[max(0.375rem,env(safe-area-inset-bottom))]' : 'pb-1.5'
                }`}
            >
                <LeistenKnopf symbol={Search} beschriftung={t.suche} aktiv={nav.modus === 'suche'} onClick={() => modusUmschalten('suche')} />
                <LeistenKnopf symbol={History} beschriftung={t.zuletzt} aktiv={nav.modus === 'zuletzt'} onClick={() => modusUmschalten('zuletzt')} />
                <LeistenKnopf symbol={Star} beschriftung={t.favoriten} aktiv={nav.modus === 'favoriten'} onClick={() => modusUmschalten('favoriten')} />
                <LeistenKnopf symbol={PenLine} beschriftung={t.freitext} onClick={onFreitext} gesperrt={sammeln} />
            </nav>
        </div>
    );
};
